import React from "react";
import { Card, Typography, Progress } from "antd";
import { Download } from "lucide-react";
import {
  Document,
  Page,
  Text as PdfText,
  View,
  StyleSheet,
  pdf,
  Image,
} from "@react-pdf/renderer";
import ReportHeader from "./ReportHeader";
import Icon from "../../../../assets/Icon";
import Utils from "../../../../utils/Utils";
import logo from "../../../../assets/logos/jobdrona.svg";

const { Title, Text } = Typography;

// ─── PDF styles ────────────────────────────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 56,
    paddingBottom: 48,
    paddingLeft: 20,
    paddingRight: 20,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#344054",
    backgroundColor: "#ffffff",
  },
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 44,
    backgroundColor: "#0F1B23",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: 20,
    paddingRight: 20,
  },
  headerTitle: {
    color: "#ffffff",
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
  },
  headerSub: {
    color: "#fff7ed",
    fontSize: 9,
    marginLeft: "auto",
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 32,
    backgroundColor: "#F9FAFB",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: 36,
    paddingRight: 36,
  },
  footerText: {
    fontSize: 8,
    color: "#98A2B3",
  },
  // Overall Summary Grid
  summaryGrid: {
    flexDirection: "row",
    marginBottom: 16,
    gap: 12,
  },
  summaryCard: {
    flex: 2,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderStyle: "solid",
    borderRadius: 6,
    padding: 14,
    backgroundColor: "#ffffff",
  },
  scoreCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderStyle: "solid",
    borderRadius: 6,
    padding: 14,
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#344054",
    marginBottom: 8,
  },
  summaryText: {
    fontSize: 9,
    color: "#475467",
    lineHeight: 1.6,
    // maxHeight: 120,
  },
  scoreCircle: {
    width: 70,
    height: 70,
    // border: "7px soild #F79009",
    borderRadius: 40,
    backgroundColor: "#F79009",
    alignItems: "center",
    justifyContent: "center",
  },
  scoreNumber: {
    color: "#ffffff",
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
  },
  scoreLabel: {
    fontSize: 8,
    color: "#667085",
    marginTop: 6,
    textAlign: "center",
  },
  card: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderStyle: "solid",
    borderRadius: 6,
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 14,
    paddingRight: 14,
    backgroundColor: "#ffffff",
  },
  cardTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#344054",
    marginBottom: 2,
  },
  cardMeta: {
    fontSize: 8,
    color: "#98A2B3",
    marginBottom: 6,
  },
  columns: {
    flexDirection: "row",
    marginTop: 8,
  },
  columnLeft: {
    flex: 1,
    marginRight: 12,
  },
  columnRight: {
    flex: 1,
  },
  columnLabel: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    marginBottom: 5,
  },
  labelGreen: { color: "#039855" },
  labelRed: { color: "#D92D20" },
  bullet: {
    flexDirection: "row",
    marginBottom: 4,
  },
  bulletDot: {
    width: 10,
    fontSize: 9,
    color: "#475467",
  },
  bulletText: {
    flex: 1,
    fontSize: 9,
    color: "#475467",
    lineHeight: 1.5,
  },
  emptyText: {
    color: "#98A2B3",
    fontSize: 9,
  },
  suggestion: {
    marginTop: 10,
    backgroundColor: "#FFFBEB",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#FCD34D",
    borderStyle: "solid",
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 10,
    paddingRight: 10,
  },
  suggestionLabel: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#DC6803",
    marginBottom: 3,
  },
  suggestionText: {
    fontSize: 9,
    color: "#DC6803",
    lineHeight: 1.5,
  },
});

// ─── PDF Document ──────────────────────────────────────────────────────────────
const ReportPDF = ({
  sections,
  overallSummary,
  overallScore,
  jobTitle,
  jobId,
  matchAnalysisIdentifier,
  resumeName,
  resumeId,
}) => (
  <Document title="Resume-Job Match Report">
    <Page size="A4" style={pdfStyles.page}>
      {/* HEADER */}
      <View style={pdfStyles.header} fixed>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Image
            src={logo}
            style={{
              width: 25,
              height: 25,
              marginRight: 8,
            }}
          />
          <View style={{ flexDirection: "column" }}>
            <PdfText style={pdfStyles.headerTitle}>
              Resume-Job Match Report
            </PdfText>
            <PdfText style={pdfStyles.headerSub}>
              Report ID: {matchAnalysisIdentifier ?? "N/A"}
            </PdfText>
          </View>
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <PdfText style={pdfStyles.headerSub}>
            {resumeName ?? "--"} ({resumeId ?? "N/A"})
          </PdfText>
          <PdfText style={pdfStyles.headerSub}>
            {jobTitle ?? "--"} ({jobId ?? "N/A"})
          </PdfText>
        </View>
      </View>

      {/* FOOTER */}
      <View style={pdfStyles.footer} fixed>
        <PdfText style={pdfStyles.footerText}>
          © {new Date().getFullYear()} Copyright JobDrona, India. All rights
          reserved.
        </PdfText>
        <PdfText
          style={pdfStyles.footerText}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      </View>

      {/* OVERALL SUMMARY GRID - Same as UI */}
      {(overallSummary || overallScore != null) && (
        <View style={pdfStyles.summaryGrid}>
          {/* Summary Text Card - 2fr */}
          <View style={pdfStyles.summaryCard}>
            <PdfText style={pdfStyles.summaryTitle}>Overall Summary</PdfText>
            <View style={pdfStyles.summaryText}>
              <PdfText>{overallSummary || "No summary available"}</PdfText>
            </View>
          </View>

          {/* Score Circle Card - 1fr */}
          {overallScore != null && (
            <View style={pdfStyles.scoreCard}>
              <View style={pdfStyles.scoreCircle}>
                <PdfText style={pdfStyles.scoreNumber}>{overallScore}%</PdfText>
              </View>
              <PdfText style={pdfStyles.scoreLabel}>Overall Score</PdfText>
            </View>
          )}
        </View>
      )}

      {/* SECTIONS */}
      {sections.map((section, idx) => (
        <View key={idx} style={pdfStyles.card}>
          <PdfText style={pdfStyles.cardTitle}>{section.title}</PdfText>
          {section.meta && (
            <PdfText style={pdfStyles.cardMeta}>{section.meta}</PdfText>
          )}

          <View style={pdfStyles.columns}>
            <View style={pdfStyles.columnLeft}>
              <PdfText style={[pdfStyles.columnLabel, pdfStyles.labelGreen]}>
                What's working:
              </PdfText>
              {section.working.length > 0 ? (
                section.working.map((item, i) => (
                  <View key={i} style={pdfStyles.bullet}>
                    <PdfText style={pdfStyles.bulletDot}>•</PdfText>
                    <PdfText style={pdfStyles.bulletText}>{item}</PdfText>
                  </View>
                ))
              ) : (
                <PdfText style={pdfStyles.emptyText}>None identified</PdfText>
              )}
            </View>

            <View style={pdfStyles.columnRight}>
              <PdfText style={[pdfStyles.columnLabel, pdfStyles.labelRed]}>
                What's missing:
              </PdfText>
              {section.missing.length > 0 ? (
                section.missing.map((item, i) => (
                  <View key={i} style={pdfStyles.bullet}>
                    <PdfText style={pdfStyles.bulletDot}>•</PdfText>
                    <PdfText style={pdfStyles.bulletText}>{item}</PdfText>
                  </View>
                ))
              ) : (
                <PdfText style={pdfStyles.emptyText}>None identified</PdfText>
              )}
            </View>
          </View>

          {section.aiSuggestion && (
            <View style={pdfStyles.suggestion}>
              <PdfText style={pdfStyles.suggestionLabel}>
                AI Suggestions:
              </PdfText>
              <PdfText style={pdfStyles.suggestionText}>
                {section.aiSuggestion}
              </PdfText>
            </View>
          )}
        </View>
      ))}
    </Page>
  </Document>
);

// ─── Main component ────────────────────────────────────────────────────────────
const ViewReportMain = ({ matchData, setLoading, userDetail }) => {
  const ar = matchData?.analysisResult;
  const profileData = Utils.getProfileData();

  const sections = [];

  // Professional Summary
  if (
    ar.professionalSummaryMatchingWithJD ||
    ar.professionalSummaryMissingForJD
  ) {
    sections.push({
      title: "Professional Summary",
      meta: null,
      working: ar.professionalSummaryMatchingWithJD
        ? [ar.professionalSummaryMatchingWithJD]
        : [],
      missing: ar.professionalSummaryMissingForJD
        ? [ar.professionalSummaryMissingForJD]
        : [],
      aiSuggestion: ar.professionalSummaryImprovementsNeeded || null,
    });
  }

  // Experiences
  (ar.experiences || []).forEach((exp) => {
    if (exp.matchingWithJD?.length || exp.missingForJD?.length) {
      sections.push({
        title: `Experience - ${exp.role}`,
        meta: [exp.company, exp.location].filter(Boolean).join(", "),
        working: exp.matchingWithJD || [],
        missing: exp.missingForJD || [],
        aiSuggestion: (exp.improvementsNeeded || []).join(" "),
      });
    }
  });

  // Education
  (ar.educations || []).forEach((edu) => {
    if (edu.matchingWithJD?.length || edu.missingForJD?.length) {
      sections.push({
        title: `Education - ${edu.degree}`,
        meta: edu.institution || null,
        working: edu.matchingWithJD || [],
        missing: edu.missingForJD || [],
        aiSuggestion: (edu.improvementsNeeded || []).join(" "),
      });
    }
  });

  // Skills
  const sk = ar.skills;
  if (sk && (sk.matchingWithJD?.length || sk.missingForJD?.length)) {
    sections.push({
      title: "Skills",
      meta: null,
      working: sk.matchingWithJD || [],
      missing: sk.missingForJD || [],
      aiSuggestion: (sk.improvementsNeeded || []).join(" "),
    });
  }

  // Projects
  (ar.projects || []).forEach((proj) => {
    if (proj.matchingWithJD?.length || proj.missingForJD?.length) {
      sections.push({
        title: `Project - ${proj.name}`,
        meta: null,
        working: proj.matchingWithJD || [],
        missing: proj.missingForJD || [],
        aiSuggestion: (proj.improvementsNeeded || []).join(" "),
      });
    }
  });

  // Certifications
  (ar.certifications || []).forEach((cert) => {
    if (!cert.name) return;
    if (cert.matchingWithJD?.length || cert.missingForJD?.length) {
      sections.push({
        title: `Certification - ${cert.name}`,
        meta: cert.issuer || null,
        working: cert.matchingWithJD || [],
        missing: cert.missingForJD || [],
        aiSuggestion: (cert.improvementsNeeded || []).join(" "),
      });
    }
  });

  // ── Download handler ─────────────────────────────────────────────────────────
  const handleDownload = async () => {
    const blob = await pdf(
      <ReportPDF
        sections={sections}
        overallSummary={ar?.overallSummary || null}
        overallScore={ar?.overallScore ?? null}
        jobTitle={matchData?.jobTitle ?? null}
        jobId={matchData?.jdIdentifier ?? null}
        matchAnalysisIdentifier={matchData?.matchAnalysisIdentifier ?? null}
        resumeName={matchData?.resumeFileName}
        resumeId={matchData?.resumeIdentifier}
      />,
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${profileData?.firstName}${profileData?.lastName}(resume-job-match-report).pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="my-3 max-w-[980px] mx-auto px-4 sm:px-6">
      <ReportHeader
        matchData={matchData}
        handleDownload={handleDownload}
        userDetail={userDetail}
        setLoading={setLoading}
      />
      <div className="report-main-content px-3 sm:px-5 py-4 space-y-4 border border-[#D0D5DD] shadow bg-[#EAECF0] rounded-b-md">
        
        {/* OVERALL SUMMARY CARDS - Same as UI */}
        {(ar?.overallSummary || ar?.overallScore != null) && (
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-y-4 gap-x-3">
            {/* Summary Text Card */}
            <Card className="h-full" bodyStyle={{ padding: "16px 20px" }}>
              <Title
                level={5}
                className="!mb-2 !text-[#344054] text-base sm:text-lg"
              >
                Overall Summary
              </Title>
              <div className="orange-scroll overflow-y-auto max-h-[10rem] pr-1">
                <Text className="text-sm text-[#475467] leading-relaxed">
                  {ar?.overallSummary || "No summary available"}
                </Text>
              </div>
            </Card>

            {/* Score Circle Card */}
            <Card
              className="h-full"
              bodyStyle={{
                padding: "16px 20px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="flex flex-col items-center justify-center w-full h-full">
                <Progress
                  type="circle"
                  percent={ar?.overallScore ?? 0}
                  size={90}
                  strokeWidth={11}
                  strokeColor="#F79009"
                  trailColor="#E4E7EC"
                  format={(p) => (
                    <span className="text-[1.1rem] font-semibold text-[#101828]">
                      {p}%
                    </span>
                  )}
                />
                <Text className="text-xs text-[#667085] mt-[.6rem] font-medium">
                  Overall Score
                </Text>
              </div>
            </Card>
          </div>
        )}

        {/* SECTIONS */}
        {sections.map((section, idx) => (
          <Card
            key={idx}
            bodyStyle={{ padding: "16px 20px" }}
            className="no-break"
          >
            <Title
              level={5}
              className="!mb-1 !text-[#344054] text-base sm:text-lg"
            >
              {section.title}
            </Title>

            {section.meta && (
              <Text className="text-xs text-[#98A2B3] block mb-3">
                {section.meta}
              </Text>
            )}

            <div className="mt-[.7rem] grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* What's working */}
              <div>
                <Text className="text-[.85rem] font-medium text-[#039855] block mb-1">
                  What's working:
                </Text>
                <div className="max-h-[16rem] md:max-h-[16rem] overflow-y-auto orange-scroll">
                  {section.working.length > 0 ? (
                    section.working.map((item, index) => (
                      <div key={index} className="py-[.15rem] px-0">
                        <Text className="text-[.85rem] text-[#475467] flex items-start gap-2">
                          <span className="text-[#475467] mt-0.5">•</span>{" "}
                          {item}
                        </Text>
                      </div>
                    ))
                  ) : (
                    <Text className="text-sm text-[#98A2B3]">
                      None identified
                    </Text>
                  )}
                </div>
              </div>

              {/* What's missing */}
              <div>
                <Text className="text-[.85rem] font-medium text-[#D92D20] block mb-1">
                  What's missing:
                </Text>
                <div className="max-h-[16rem] md:max-h-[16rem] overflow-y-auto orange-scroll">
                  {section.missing.length > 0 ? (
                    section.missing.map((item, index) => (
                      <div key={index} className="py-[.15rem] px-0">
                        <Text className="text-[.85rem] text-[#475467] flex items-start gap-2">
                          <span className="text-[#475467] mt-0.5">•</span>{" "}
                          {item}
                        </Text>
                      </div>
                    ))
                  ) : (
                    <Text className="text-sm text-[#98A2B3]">
                      None identified
                    </Text>
                  )}
                </div>
              </div>
            </div>

            {/* AI Suggestion */}
            {section.aiSuggestion && (
              <div className="mt-4 bg-amber-50 border border-amber-300 rounded-md px-4 py-3">
                <Text className="text-[#DC6803] font-semibold text-sm block mb-1">
                  AI Suggestions:
                </Text>
                <Text className="text-[#DC6803] text-sm">
                  {section.aiSuggestion}
                </Text>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ViewReportMain;